/** Server-Sent Events from /app/v1/events/stream (ADR 0003 rule 4). On any committed domain
 *  event the affected cached reads are refreshed; the server already filtered by audience. */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export type RealtimeEvent = { event_id: string; type: string; aggregate_type: string; aggregate_id: string };

const REFRESH: Record<string, string[][]> = {
  creative_request: [["requests"], ["request"], ["dashboard"], ["notifications"], ["projects"], ["project"], ["strategy"], ["karya"], ["plan"], ["studio"], ["package"]],
  production_job: [["plan"], ["project"], ["dashboard"], ["jobs"], ["job"], ["review"], ["reviews"]],
  media_asset: [["assets"], ["asset"], ["job"]],
  diagnostic: [["notifications"]],
};

const COALESCE_MS = 250;

export function useRealtime(enabled: boolean) {
  const [connected, setConnected] = useState(false);
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled || typeof EventSource === "undefined") return;
    const es = new EventSource("/app/v1/events/stream", { withCredentials: true });
    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);
    // Bursts (relay catch-up, media derivatives) are coalesced into one refresh per window, and an
    // in-flight read is never cancelled (cancelling on every event starves a query under load).
    // A key whose read is still in flight stays pending and is refreshed again after it lands,
    // so no event is ever absorbed by a read that started before it.
    const pending = new Map<string, string[]>();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const flush = () => {
      timer = undefined;
      for (const [id, key] of pending) {
        if (qc.isFetching({ queryKey: key }) > 0) continue;
        pending.delete(id);
        void qc.invalidateQueries({ queryKey: key }, { cancelRefetch: false });
      }
      if (pending.size > 0) timer = setTimeout(flush, COALESCE_MS);
    };
    const onEvent = (m: MessageEvent) => {
      const ev = JSON.parse(m.data) as RealtimeEvent;
      for (const key of REFRESH[ev.aggregate_type] ?? [["dashboard"], ["notifications"]]) pending.set(key.join("/"), key);
      timer ??= setTimeout(flush, COALESCE_MS);
    };
    es.onmessage = onEvent;
    return () => { es.close(); if (timer) clearTimeout(timer); };
  }, [enabled, qc]);
  return connected;
}
